/** Input area per node type (AGENTS.md 5.3, 6.3). Every control is keyboard-operable with 44px targets. */
import { useEffect, useRef, useState } from "preact/hooks";
import type { FlatNode } from "@wapadesk/engine";
import { renderTemplate } from "@wapadesk/engine";
import type { WidgetConfigResponse } from "@wapadesk/schema";
import { t } from "@wapadesk/i18n";
import type { UploadedFile } from "../store";

export interface InputProps {
  node: FlatNode;
  locale: string;
  clinicName: string;
  config: WidgetConfigResponse;
  initial?: unknown;
  files: UploadedFile[];
  error?: string;
  busy?: boolean;
  onSubmit: (value: unknown) => void;
  onSkip?: () => void;
  onDecline?: () => void;
  onUpload?: (file: File) => Promise<void>;
}

export const COUNTRIES: Array<[string, string, string]> = [
  ["GB", "+44", "United Kingdom"],
  ["TR", "+90", "Türkiye"],
  ["IE", "+353", "Ireland"],
  ["US", "+1", "United States"],
  ["DE", "+49", "Germany"],
  ["FR", "+33", "France"],
  ["NL", "+31", "Netherlands"],
  ["ES", "+34", "Spain"],
  ["IT", "+39", "Italy"],
  ["AE", "+971", "United Arab Emirates"],
  ["SA", "+966", "Saudi Arabia"],
];

function ErrorText({ error, locale }: { error?: string; locale: string }) {
  if (!error) return null;
  const key = `validation.${error}` as Parameters<typeof t>[1];
  const msg = t(locale, key) === undefined ? error : t(locale, key);
  return (
    <p class="err" role="alert">
      {msg.startsWith("validation.") ? error : msg}
    </p>
  );
}

export function TextInput(p: InputProps) {
  const n = p.node.node;
  const long = n.type === "long_text";
  const [v, setV] = useState(typeof p.initial === "string" ? p.initial : "");
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), [n.id]);
  const max = "maxLength" in n && n.maxLength ? n.maxLength : long ? 800 : 300;
  const type =
    n.type === "email"
      ? "email"
      : n.type === "number"
        ? "text"
        : n.type === "date"
          ? "date"
          : "text";
  const submit = (e: Event) => {
    e.preventDefault();
    p.onSubmit(n.type === "number" ? v.trim() : v);
  };
  return (
    <form class="in" onSubmit={submit}>
      <label class="sr" for={`in-${n.id}`}>
        {t(p.locale, "chrome.yourAnswer")}
      </label>
      {long ? (
        <textarea
          id={`in-${n.id}`}
          ref={ref}
          rows={3}
          maxLength={max}
          value={v}
          onInput={(e) => setV((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
          }}
        />
      ) : (
        <input
          id={`in-${n.id}`}
          ref={ref}
          type={type}
          inputMode={n.type === "number" ? "decimal" : n.type === "email" ? "email" : "text"}
          maxLength={max}
          value={v}
          autocomplete={
            n.type === "email" ? "email" : n.maps === "contact.fullName" ? "name" : "off"
          }
          onInput={(e) => setV((e.target as HTMLInputElement).value)}
        />
      )}
      <ErrorText error={p.error} locale={p.locale} />
      <div class="row">
        {!n.required && p.onSkip && (
          <button type="button" class="ghost" onClick={p.onSkip}>
            {t(p.locale, "chrome.skip")}
          </button>
        )}
        <button type="submit" class="primary" disabled={p.busy}>
          {t(p.locale, "chrome.send")}
        </button>
      </div>
    </form>
  );
}

export function PhoneInput(p: InputProps) {
  const n = p.node.node;
  const def = "defaultCountry" in n && n.defaultCountry ? n.defaultCountry : "GB";
  const init = typeof p.initial === "string" ? p.initial : "";
  const initCountry = COUNTRIES.find((c) => init.startsWith(c[1]))?.[0] ?? def;
  const [cc, setCc] = useState(initCountry);
  const [num, setNum] = useState(
    init ? init.replace(COUNTRIES.find((c) => c[0] === initCountry)?.[1] ?? "", "") : "",
  );
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), [n.id]);
  const submit = (e: Event) => {
    e.preventDefault();
    const dial = COUNTRIES.find((c) => c[0] === cc)?.[1] ?? "+44";
    const digits = num.replace(/[\s()-]/g, "");
    p.onSubmit(digits.startsWith("+") ? digits : dial + digits.replace(/^0+/, ""));
  };
  return (
    <form class="in" onSubmit={submit}>
      <div class="phone">
        <label class="sr" for={`cc-${n.id}`}>
          {t(p.locale, "chrome.selectCountry")}
        </label>
        <select
          id={`cc-${n.id}`}
          value={cc}
          onChange={(e) => setCc((e.target as HTMLSelectElement).value)}
        >
          {COUNTRIES.map(([code, dial, name]) => (
            <option key={code} value={code}>
              {name} {dial}
            </option>
          ))}
        </select>
        <label class="sr" for={`in-${n.id}`}>
          {t(p.locale, "chrome.yourAnswer")}
        </label>
        <input
          id={`in-${n.id}`}
          ref={ref}
          type="tel"
          inputMode="tel"
          autocomplete="tel-national"
          value={num}
          onInput={(e) => setNum((e.target as HTMLInputElement).value)}
        />
      </div>
      <ErrorText error={p.error} locale={p.locale} />
      <div class="row">
        {!n.required && p.onSkip && (
          <button type="button" class="ghost" onClick={p.onSkip}>
            {t(p.locale, "chrome.skip")}
          </button>
        )}
        <button type="submit" class="primary" disabled={p.busy}>
          {t(p.locale, "chrome.send")}
        </button>
      </div>
    </form>
  );
}

export function ChoiceInput(p: InputProps) {
  const n = p.node.node;
  if (n.type !== "single_choice" && n.type !== "multi_choice") return null;
  const multi = n.type === "multi_choice";
  const init = Array.isArray(p.initial)
    ? (p.initial as string[])
    : typeof p.initial === "string" && p.initial
      ? [p.initial]
      : [];
  const [sel, setSel] = useState<string[]>(init.filter((x) => !x.startsWith("other:")));
  const initOther = init.find((x) => x.startsWith("other:"));
  const [other, setOther] = useState(initOther ? initOther.slice(6) : "");
  const [otherOn, setOtherOn] = useState(Boolean(initOther));
  const toggle = (id: string) => {
    if (multi) setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    else {
      setOtherOn(false);
      p.onSubmit(id);
    }
  };
  const submit = (e: Event) => {
    e.preventDefault();
    const vals = [...sel, ...(otherOn && other.trim() ? [`other:${other.trim()}`] : [])];
    p.onSubmit(multi ? vals : (vals[0] ?? ""));
  };
  return (
    <form class="in" onSubmit={submit}>
      <div
        class="chips"
        role={multi ? "group" : "radiogroup"}
        aria-label={t(p.locale, "chrome.yourAnswer")}
      >
        {n.options.map((o) => (
          <button
            key={o.id}
            type="button"
            class={`chip ${sel.includes(o.id) ? "on" : ""}`}
            role={multi ? "checkbox" : "radio"}
            aria-checked={sel.includes(o.id)}
            onClick={() => toggle(o.id)}
          >
            {o.label}
          </button>
        ))}
        {n.allowOther && (
          <button
            type="button"
            class={`chip ${otherOn ? "on" : ""}`}
            role={multi ? "checkbox" : "radio"}
            aria-checked={otherOn}
            onClick={() => setOtherOn((v) => !v)}
          >
            {t(p.locale, "chrome.other")}
          </button>
        )}
      </div>
      {otherOn && (
        <input
          type="text"
          maxLength={300}
          placeholder={t(p.locale, "chrome.otherPlaceholder")}
          value={other}
          onInput={(e) => setOther((e.target as HTMLInputElement).value)}
          aria-label={t(p.locale, "chrome.other")}
        />
      )}
      <ErrorText error={p.error} locale={p.locale} />
      {(multi || otherOn) && (
        <div class="row">
          {!n.required && p.onSkip && (
            <button type="button" class="ghost" onClick={p.onSkip}>
              {t(p.locale, "chrome.skip")}
            </button>
          )}
          <button type="submit" class="primary" disabled={p.busy}>
            {t(p.locale, "chrome.send")}
          </button>
        </div>
      )}
      {!multi && !otherOn && !n.required && p.onSkip && (
        <div class="row">
          <button type="button" class="ghost" onClick={p.onSkip}>
            {t(p.locale, "chrome.skip")}
          </button>
        </div>
      )}
    </form>
  );
}

/** A heading whose only procedure carries the heading's own name ("Mole mapping"). */
const directOf = (cat: string, items: WidgetConfigResponse["procedures"]) =>
  items.length === 1 && items[0]!.name.trim().toLowerCase() === cat.trim().toLowerCase()
    ? items[0]!.id
    : undefined;

export function ProcedureInput(p: InputProps) {
  const n = p.node.node;
  if (n.type !== "procedure_picker") return null;
  const init = Array.isArray(p.initial) ? (p.initial as string[]) : [];
  const [sel, setSel] = useState<string[]>(init);
  const groups = new Map<string, WidgetConfigResponse["procedures"]>();
  for (const pr of p.config.procedures) {
    if (n.categories?.length && !n.categories.includes(pr.category)) continue;
    groups.set(pr.category, [...(groups.get(pr.category) ?? []), pr]);
  }
  // Main heading first (owner request, 14 October 2026), only when there is a choice to make.
  const groupFirst = Boolean(n.groupFirst) && groups.size > 1;
  const catOf = (id: string) => p.config.procedures.find((x) => x.id === id)?.category;
  const [cats, setCats] = useState<string[]>(() => [
    ...new Set(init.map(catOf).filter((c): c is string => Boolean(c))),
  ]);
  const [stage, setStage] = useState<"headings" | "items">(
    groupFirst && init.length === 0 ? "headings" : "items",
  );
  const toggle = (id: string) => {
    if (n.multiple) setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    else p.onSubmit([id]);
  };
  const submit = (e: Event) => {
    e.preventDefault();
    p.onSubmit(sel);
  };
  const toItems = (chosen: string[]) => {
    const direct = chosen
      .map((c) => directOf(c, groups.get(c) ?? []))
      .filter((x): x is string => Boolean(x));
    // Every chosen heading is a procedure in itself: nothing more to ask.
    if (direct.length === chosen.length) return p.onSubmit(direct);
    setSel((s) => [...new Set([...s, ...direct])]);
    setCats(chosen);
    setStage("items");
  };

  if (stage === "headings")
    return (
      <form
        class="in"
        onSubmit={(e) => {
          e.preventDefault();
          if (cats.length) toItems(cats);
        }}
      >
        <div class="chips" role="group" aria-label={n.prompt}>
          {[...groups.keys()].map((cat) => (
            <button
              key={cat}
              type="button"
              class={`chip ${cats.includes(cat) ? "on" : ""}`}
              {...(n.multiple ? { role: "checkbox", "aria-checked": cats.includes(cat) } : {})}
              onClick={() =>
                n.multiple
                  ? setCats((c) => (c.includes(cat) ? c.filter((x) => x !== cat) : [...c, cat]))
                  : toItems([cat])
              }
            >
              {cat}
            </button>
          ))}
        </div>
        <ErrorText error={p.error} locale={p.locale} />
        <div class="row">
          {!n.required && p.onSkip && (
            <button type="button" class="ghost" onClick={p.onSkip}>
              {t(p.locale, "chrome.skip")}
            </button>
          )}
          {n.multiple && (
            <button type="submit" class="primary" disabled={p.busy || cats.length === 0}>
              {t(p.locale, "chrome.send")}
            </button>
          )}
        </div>
      </form>
    );

  const shown = [...groups.entries()].filter(
    ([cat]) => !groupFirst || cats.length === 0 || cats.includes(cat),
  );
  return (
    <form class="in" onSubmit={submit}>
      {groupFirst && (
        <button
          type="button"
          class="link-btn"
          onClick={() => {
            setStage("headings");
          }}
        >
          ← {t(p.locale, "chrome.allHeadings")}
        </button>
      )}
      {shown.map(([cat, items]) => (
        <div key={cat} class="group" role="group" aria-label={cat}>
          <div class="cat">{cat}</div>
          <div class="chips">
            {items.map((pr) => (
              <button
                key={pr.id}
                type="button"
                class={`chip ${sel.includes(pr.id) ? "on" : ""}`}
                role="checkbox"
                aria-checked={sel.includes(pr.id)}
                onClick={() => toggle(pr.id)}
              >
                {pr.name}
              </button>
            ))}
          </div>
        </div>
      ))}
      <ErrorText error={p.error} locale={p.locale} />
      <div class="row">
        {!n.required && p.onSkip && (
          <button type="button" class="ghost" onClick={p.onSkip}>
            {t(p.locale, "chrome.skip")}
          </button>
        )}
        {n.multiple && (
          <button type="submit" class="primary" disabled={p.busy || sel.length === 0}>
            {t(p.locale, "chrome.send")}
          </button>
        )}
      </div>
    </form>
  );
}

export function ConsentInput(p: InputProps) {
  const n = p.node.node;
  if (n.type !== "consent") return null;
  const [checked, setChecked] = useState<Record<string, boolean>>(
    Object.fromEntries(n.items.map((i) => [i.key, false])),
  );
  const ctx = { clinic: { name: p.clinicName }, answers: {} };
  const submit = (e: Event) => {
    e.preventDefault();
    p.onSubmit(checked);
  };
  return (
    <form class="in consent" onSubmit={submit}>
      {n.items.map((i) => (
        <label key={i.key} class="check">
          <input
            type="checkbox"
            checked={checked[i.key] ?? false}
            required={i.required}
            onChange={(e) =>
              setChecked((c) => ({ ...c, [i.key]: (e.target as HTMLInputElement).checked }))
            }
          />
          <span>
            {renderTemplate(i.text, ctx)}
            {i.required ? " *" : ""}
          </span>
        </label>
      ))}
      {n.policyUrl && (
        <a class="link" href={n.policyUrl} target="_blank" rel="noopener">
          {t(p.locale, "chrome.privacyPolicy")}
        </a>
      )}
      <ErrorText error={p.error} locale={p.locale} />
      <div class="row">
        {p.onDecline && (
          <button type="button" class="ghost" onClick={p.onDecline}>
            {t(p.locale, "chrome.decline")}
          </button>
        )}
        <button type="submit" class="primary" disabled={p.busy}>
          {t(p.locale, "chrome.accept")}
        </button>
      </div>
    </form>
  );
}

export function FileInput(p: InputProps) {
  const n = p.node.node;
  if (n.type !== "file_upload") return null;
  const [err, setErr] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const mine = p.files.filter((f) => f.nodeId === n.id);
  const onFiles = async (list: FileList | null) => {
    if (!list || !p.onUpload) return;
    setErr(undefined);
    for (const f of Array.from(list)) {
      if (mine.length >= n.maxFiles) break;
      if (f.size > n.maxSizeMb * 1024 * 1024) {
        setErr("too_large");
        continue;
      }
      try {
        await p.onUpload(f);
      } catch {
        setErr("upload_failed");
      }
    }
  };
  const done = (e: Event) => {
    e.preventDefault();
    p.onSubmit(mine.filter((f) => f.status === "done").map((f) => f.fileId));
  };
  return (
    <form class="in" onSubmit={done}>
      <input
        ref={inputRef}
        class="sr"
        type="file"
        multiple
        accept={n.accept.join(",")}
        onChange={(e) => void onFiles((e.target as HTMLInputElement).files)}
        aria-label={t(p.locale, "chrome.addFiles")}
      />
      <input
        ref={cameraRef}
        class="sr"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => void onFiles((e.target as HTMLInputElement).files)}
        aria-label={t(p.locale, "chrome.takePhoto")}
      />
      <div class="row">
        <button
          type="button"
          class="ghost"
          onClick={() => inputRef.current?.click()}
          disabled={mine.length >= n.maxFiles}
        >
          {t(p.locale, "chrome.addFiles")}
        </button>
        <button
          type="button"
          class="ghost"
          onClick={() => cameraRef.current?.click()}
          disabled={mine.length >= n.maxFiles}
        >
          {t(p.locale, "chrome.takePhoto")}
        </button>
      </div>
      {mine.length > 0 && (
        <ul class="files" aria-live="polite">
          {mine.map((f) => (
            <li key={f.fileId}>
              <span>{f.name || f.contentType}</span>
              <span class="muted">
                {f.status === "uploading"
                  ? t(p.locale, "chrome.uploading")
                  : f.status === "failed"
                    ? t(p.locale, "chrome.uploadFailed")
                    : t(p.locale, "chrome.saved")}
              </span>
            </li>
          ))}
        </ul>
      )}
      {err === "too_large" && <p class="err" role="alert">{`Max ${n.maxSizeMb} MB`}</p>}
      {err === "upload_failed" && (
        <p class="err" role="alert">
          {t(p.locale, "chrome.uploadFailed")}
        </p>
      )}
      <ErrorText error={p.error} locale={p.locale} />
      <div class="row">
        {!n.required && p.onSkip && mine.length === 0 && (
          <button type="button" class="ghost" onClick={p.onSkip}>
            {t(p.locale, "chrome.skip")}
          </button>
        )}
        <button
          type="submit"
          class="primary"
          disabled={
            p.busy ||
            mine.some((f) => f.status === "uploading") ||
            (n.required && mine.length === 0)
          }
        >
          {t(p.locale, "chrome.done")}
        </button>
      </div>
    </form>
  );
}

export function InputFor(p: InputProps) {
  switch (p.node.node.type) {
    case "consent":
      return <ConsentInput {...p} />;
    case "phone":
      return <PhoneInput {...p} />;
    case "single_choice":
    case "multi_choice":
      return <ChoiceInput {...p} />;
    case "procedure_picker":
      return <ProcedureInput {...p} />;
    case "file_upload":
      return <FileInput {...p} />;
    case "text":
    case "long_text":
    case "email":
    case "number":
    case "date":
      return <TextInput {...p} />;
    default:
      return null;
  }
}
